import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, UntypedFormControl} from '@angular/forms';
import {firstValueFrom, Observable, of, switchMap} from 'rxjs';
import {debounceTime, filter, map, startWith} from 'rxjs/operators';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatAutocompleteModule, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {AsyncPipe, LocationStrategy, NgForOf, NgIf} from '@angular/common';
import {MatDividerModule} from '@angular/material/divider';
import {Router} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {Store} from '@ngrx/store';
import {Entity} from '../../../../../models/Entity';
import {CdxData} from '../../../../../models/cdx-data';
import {TaskFormField} from '../../../../../models/task-form-field';
import {TaskContactTypeList} from '../../../../../models/workflow/taskContact';
import {TaskDetailsService} from '../../../../../redux/details/service/task/task-details.service';
import {EsPage} from '../../../../../models/es-page';
import {MatOptionSelectionChange} from '@angular/material/core';
import {SocketService, TRANSACTION_ID_QP} from '../../../../../services/socket/socket.service';
import {SocketData, SocketDataType} from '../../../../../models/SocketData';
import {Utils} from '../../../../../utils/utils';
import {AuthenticationService} from '../../../../../../entry-routes/authentication/authentication.service';
import {DomainItem} from '../../../../../models/domain-item';
import {selectDomainsDatas} from '../../../../../redux/domain/selectors/domain.selectors';
import {ButtonComponent} from '../../../../buttons/button/button.component';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    AsyncPipe,
    NgIf,
    MatDividerModule,
    NgForOf,
    TranslateModule,
    ButtonComponent
  ]
})

export class ContactComponent implements OnInit {

  @Input()
  taskInstanceId: string;

  @Input()
  token: string;

  @Output()
  hasChanged: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('contactInput')
  contactInput: MatAutocompleteTrigger;

  taskFormField: TaskFormField = null;

  contactForm: FormGroup;

  contactTypes: TaskContactTypeList[] = [];

  currentContacts: Entity[] = [];
  filteredContacts$: Observable<Entity[]>;

  transactionIdToAdd: string;
  canCreateContact = false;
  private domainItems: DomainItem[] = [];

  private contactTypeCtrl: UntypedFormControl;
  private contactCtrl: UntypedFormControl;

  constructor(
    private taskDetailsService: TaskDetailsService,
    private router: Router,
    private locationStrategy: LocationStrategy,
    private socketService: SocketService,
    private authenticationService: AuthenticationService,
    private store: Store,
  ) {
    this.contactTypeCtrl = new FormControl({ value: '', disabled: false });
    this.contactCtrl = new FormControl({ value: '', disabled: true });

    this.contactForm = new FormGroup({
      contactType: this.contactTypeCtrl,
      contact: this.contactCtrl
    });

    this.filteredContacts$ = this.contactCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      switchMap(value => {
        if (typeof value === 'string'){
          return this._filter(value);
        }else{
          return of([value]);
        }
      })
    );
  }

  ngOnInit() {
    this.contactTypeCtrl?.valueChanges.subscribe(contactType => {
      this.onContactTypeChange(contactType);
    });

    this.taskDetailsService.getContactTypeList().subscribe((response: TaskContactTypeList[]) => {
      this.contactTypes = response;
    });

    this.socketService.currentUserEvent$.subscribe(async (event: SocketData) => {
      if (
        event.type !== SocketDataType.ENTITY ||
        event.datas.transactionId !== this.transactionIdToAdd
      ) return;

      const contact: Entity = await firstValueFrom(this.taskDetailsService.getContact('et_contact', event.id));
      this.contactCtrl?.setValue(contact);
      this.hasChanged.emit(contact);
      this.contactForm.reset();
    });

    this.store.select(selectDomainsDatas).pipe(
      filter((domainItems: DomainItem[]) => !!domainItems && domainItems.length > 0)
    ).subscribe((domainItems: DomainItem[]) => {
      this.domainItems = domainItems;
      const contactDomain = domainItems.find((domainItem: DomainItem) => domainItem.entityType === 'et_contact');
      this.canCreateContact = this.authenticationService.checkForIndexInDomain(contactDomain);
    });
  }

  private onContactTypeChange(contactType: TaskContactTypeList) {
    if (contactType) {
      this.contactCtrl?.enable();
      this.contactCtrl?.setValue('');

      this.loadContactsForContactType(contactType)
        .then(() => {
          this.contactInput.openPanel();
          this.contactCtrl?.updateValueAndValidity();
        })
    }
    else {
      this.contactCtrl?.disable();
      this.contactCtrl?.setValue('');
      this.currentContacts = [];
    }
  }

  private async loadContactsForContactType(contactType: TaskContactTypeList): Promise<void> {
    await firstValueFrom(
      this.taskDetailsService.getContacts(contactType, this.contactCtrl.value)
    ).then((page: EsPage<Entity>) => {
      this.currentContacts = page.content;
    });
  }

  private _filter(value: string): Observable<Entity[]> {
    const filterValue = !value ? '' : value.toLowerCase();

    return this.taskDetailsService.getContacts(this.contactTypeCtrl?.value, filterValue)
      .pipe(
        map(page => page.content)
      )

    // return this.currentContacts.filter(contact =>
    //   contact.cdx_title.toLowerCase().includes(filterValue) ||
    //   contact.cdx_subtitle.toLowerCase().includes(filterValue)
    // );
  }

  displayContact(contact: Entity): string {
    if (!contact) return '';
    return contact.cdx_title + (contact.cdx_subtitle ? ' ' + contact.cdx_subtitle : '');
  }

  getAddressLabel(cdx_datas: { [p: string]: CdxData }): string {
    if (!cdx_datas) return '';
    const parts = [cdx_datas['fd_adresse'], cdx_datas['fd_cp'], cdx_datas['fd_ville']].filter(Boolean);
    return parts.join(' ').trim();
  }

  openNewContactFromSelect(event: MatOptionSelectionChange) {
    if (!event.isUserInput) return;

    const transactionId = Utils.uuid();
    this.transactionIdToAdd = transactionId;

    const tree = this.router.createUrlTree(
      ['/entities/et_contact/new'],
      { queryParams: { [TRANSACTION_ID_QP]: transactionId } }
    );
    const url  = this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(tree));

    window.open(url, '_blank', 'noopener');
    event.source.deselect();
  }

  async onSave() {
    if (this.isFormValid) {
      const contact: Entity = await firstValueFrom(this.taskDetailsService.getContact(this.contactCtrl.value.cdx_type.code, this.contactCtrl.value.cdx_id));
      this.hasChanged.emit(contact);
      this.contactForm.reset();
    }
  }

  get isContactDisabled(): boolean {
    return !this.contactTypeCtrl?.value;
  }

  get isFormValid(): boolean {
    return this.contactForm.valid && !!this.contactCtrl?.value;
  }
}
